'use client';

import { Container } from 'components/shared';
import React from 'react';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { UHClueTitle, UHClueSubtitle } from './typography';
import styles from './WhosItFor.module.css';

type AudienceCard = {
  title: string;
  description: string;
  iconUrl?: string;
  iconBg: string;
};

const items: AudienceCard[] = [
  {
    title: 'Understanding your cycle health',
    description:
      'Track your period, cycle phases, and plan ahead with confidence.',
    iconUrl: '/web_v2/uh-x-clue/for-whom/card-icons/droplet.png',
    iconBg: '#E13336',
  },
  {
    title: 'Trying to conceive',
    description:
      'Know your fertile window and get science-backed support for conception.',
    iconUrl: '/web_v2/uh-x-clue/for-whom/card-icons/preg-test.png',
    iconBg: '#3E77C1',
  },
  {
    title: 'Pregnancy insights',
    description: 'Follow your pregnancy journey with week-by-week insights.',
    iconUrl: '/web_v2/uh-x-clue/for-whom/card-icons/preg-insight.png',
    iconBg: '#F56E00',
  },
  {
    title: 'Perimenopause guidance',
    description:
      'Navigate changes smoothly with tailored tracking and insights.',
    iconUrl: '/web_v2/uh-x-clue/for-whom/card-icons/peri-guidance.png',
    iconBg: '#7EB28B',
  },
];

const WhosItFor = () => {
  const cardRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const headingRef = React.useRef<HTMLDivElement | null>(null);
  const [activeMap, setActiveMap] = React.useState<Record<number, boolean>>({});
  const [uniformCardHeight, setUniformCardHeight] = React.useState<
    number | null
  >(null);

  React.useEffect(() => {
    if (!cardRefs.current.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const updates: Record<number, boolean> = {};
        for (const entry of entries) {
          const idxAttr = (entry.target as HTMLElement).getAttribute(
            'data-index',
          );
          const idx = idxAttr ? parseInt(idxAttr, 10) : -1;
          updates[idx] = entry.intersectionRatio >= 0.6;
        }
        setActiveMap((prev) => ({ ...prev, ...updates }));
      },
      { threshold: [0, 0.5, 1] },
    );

    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Ensure all cards have a uniform height based on the tallest card
  React.useEffect(() => {
    const recalcHeights = () => {
      const heights = cardRefs.current.map((el) => el?.offsetHeight || 0);
      const maxHeight = heights.length ? Math.max(...heights) : 0;
      setUniformCardHeight(maxHeight || null);
    };

    // Initial calculation (after paint)
    const raf = requestAnimationFrame(() => recalcHeights());

    // Recalculate on resize
    window.addEventListener('resize', recalcHeights);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', recalcHeights);
    };
  }, []);

  return (
    <section
      className={styles.section}
      role="region"
      aria-labelledby="whositfor-heading"
    >
      <Container>
        <div className={styles.inner}>
          <div className={styles.grid}>
            <div className={styles.headingWrap} ref={headingRef}>
              <UHClueTitle
                className={styles.headingTitle}
                id="whositfor-heading"
                $desktopColor="#000"
              >
                Who is this for?
              </UHClueTitle>
              <UHClueSubtitle className={styles.headingSubtitle}>
                Built for every stage, Ultrahuman + Clue gives you powerful
                cycle insights to guide you through periods, fertility,
                pregnancy, perimenopause, and beyond.
              </UHClueSubtitle>
              <div className={styles.fadedOverlay} />
            </div>
            <div className={styles.cards}>
              {items.map((item, index) => (
                <div
                  className={styles.card}
                  key={item.title}
                  ref={(el) => {
                    cardRefs.current[index] = el;
                  }}
                  data-index={index}
                  data-active={activeMap[index] ? 'true' : 'false'}
                  style={{ minHeight: uniformCardHeight ?? undefined }}
                >
                  <div
                    className={styles.icon}
                    style={
                      { ['--icon-bg' as string]: item.iconBg } as React.CSSProperties
                    }
                  >
                    {item.iconUrl && (
                      <img
                        className={styles.iconImage}
                        src={getCompressedAssetUrl(item.iconUrl)}
                        alt={item.title}
                        width={68}
                        height={68}
                      />
                    )}
                  </div>
                  <div className={styles.cardText}>
                    <h3 className={styles.cardTitle}>{item.title}</h3>
                    <p className={styles.cardSubtitle}>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default WhosItFor;
